/* eslint-disable no-irregular-whitespace */
// There's python code embedded below that includes non-printing characters like
// NBSP. eslint rightfully doesn't like these characters, but in this case we
// want them.
import React, {ReactNode} from 'react';

// eslint-disable-next-line import/no-cycle -- please fix if you can
import {QuickStartIcon} from './QuickStartIcon';

export enum QuickStartAnalyticsOrigin {
  QUICKSTART_PAGE = 'quickstart page',
  PROJECT_PAGE = 'project page',
  TEAM_PAGE = 'team page',
}

export enum FrameworkCategory {
  MODELS = 'models',
  LLMS = 'LLMs',
}

export enum FrameworkName {
  PYTHON_PYTORCH = 'Python/PyTorch',
  PYTORCH_LIGHTNING = 'PyTorch Lightning',
  KERAS_TENSOR_FLOW = 'Keras/TensorFlow',
  TRANSFORMERS = 'Transformers',
  XG_BOOST = 'XGBoost',
  SCIKIT_LEARN = 'Scikit-learn',
  LANGCHAIN = 'LangChain',
  OPENAI = 'OpenAI API',
  LLAMAINDEX = 'LlamaIndex',
}

export enum FrameworkPathname {
  PYTHON_PYTORCH = 'python-pytorch',
  PYTORCH_LIGHTNING = 'pytorch-lightning',
  KERAS_TENSOR_FLOW = 'keras-tensorflow',
  TRANSFORMERS = 'transformers',
  XG_BOOST = 'xgboost',
  SCIKIT_LEARN = 'scikit-learn',
  LANGCHAIN = 'langchain',
  OPENAI = 'openai',
  LLamaIndex = 'llamaindex',
}

export const FRAMEWORK_PATHNAME_MAP = new Map<FrameworkName, FrameworkPathname>(
  [
    [FrameworkName.PYTHON_PYTORCH, FrameworkPathname.PYTHON_PYTORCH],
    [FrameworkName.PYTORCH_LIGHTNING, FrameworkPathname.PYTORCH_LIGHTNING],
    [FrameworkName.KERAS_TENSOR_FLOW, FrameworkPathname.KERAS_TENSOR_FLOW],
    [FrameworkName.TRANSFORMERS, FrameworkPathname.TRANSFORMERS],
    [FrameworkName.XG_BOOST, FrameworkPathname.XG_BOOST],
    [FrameworkName.SCIKIT_LEARN, FrameworkPathname.SCIKIT_LEARN],
  ]
);

export interface Framework {
  iconWithName?: ReactNode;
  value: FrameworkName;
  codeSnippet: string;
  dataTest: string;
  pathname: FrameworkPathname;
  colabLink: string;
  frameworkCategory: FrameworkCategory;
}

export const FRAMEWORKS: Framework[] = [
  {
    iconWithName: (
      <>
        <QuickStartIcon name="python-logo" />
        <span>Python / </span>
        <QuickStartIcon name="pytorch-logo" />
        <span>Pytorch</span>
      </>
    ),
    value: FrameworkName.PYTHON_PYTORCH,
    dataTest: 'quickstart-experiment-python-pytorch',
    pathname: FrameworkPathname.PYTHON_PYTORCH,
    frameworkCategory: FrameworkCategory.MODELS,
    codeSnippet: `import random

import wandb

# Start a new wandb run to track this script.
run = wandb.init(
    # Set the wandb entity where your project will be logged (generally your team name).
    entity="my-awesome-team-name",
    # Set the wandb project where this run will be logged.
    project="my-awesome-project",
    # Track hyperparameters and run metadata.
    config={
        "learning_rate": 0.02,
        "architecture": "CNN",
        "dataset": "CIFAR-100",
        "epochs": 10,
    },
)

# Simulate training.
epochs = 10
offset = random.random() / 5
for epoch in range(2, epochs):
    acc = 1 - 2**-epoch - random.random() / epoch - offset
    loss = 2**-epoch + random.random() / epoch + offset

    # Log metrics to wandb.
    run.log({"acc": acc, "loss": loss})

# Finish the run and upload any remaining data.
run.finish()`,
    colabLink: 'https://wandb.me/intro',
  },
  {
    iconWithName: (
      <>
        <QuickStartIcon name="pytorch-lightning-logo" />
        <span>Pytorch Lightning</span>
      </>
    ),
    value: FrameworkName.PYTORCH_LIGHTNING,
    dataTest: 'quickstart-experiment-pytorch-lightning',
    pathname: FrameworkPathname.PYTORCH_LIGHTNING,
    frameworkCategory: FrameworkCategory.MODELS,
    codeSnippet: `# This script needs these libraries to be installed:
#   torch, torchvision, pytorch_lightning

import wandb

import os
from torch import optim, nn, utils, Tensor
from torchvision.datasets import MNIST
from torchvision.transforms import ToTensor

import pytorch_lightning as pl
from pytorch_lightning.loggers import WandbLogger


class LitAutoEncoder(pl.LightningModule):
    def __init__(self, lr=1e-3, inp_size=28, optimizer = 'Adam'):
        super().__init__()

        self.encoder = nn.Sequential(nn.Linear(inp_size * inp_size, 64), nn.ReLU(), nn.Linear(64, 3))
        self.decoder = nn.Sequential(nn.Linear(3, 64), nn.ReLU(), nn.Linear(64, inp_size * inp_size))
        self.lr = lr

        # save hyper-parameters to self.hparamsm auto-logged by wandb
        self.save_hyperparameters()

    def training_step(self, batch, batch_idx):
        x, y = batch
        x = x.view(x.size(0), -1)
        z = self.encoder(x)
        x_hat = self.decoder(z)
        loss = nn.functional.mse_loss(x_hat, x)

        # log metrics to wandb
        self.log("train_loss", loss)
        return loss

    def configure_optimizers(self):
        optimizer = optim.Adam(self.parameters(), lr=self.lr)
        return optimizer


# init the autoencoder
autoencoder = LitAutoEncoder(lr=1e-3, inp_size=28)

# setup data
batch_size = 32
dataset = MNIST(os.getcwd(), download=True, transform=ToTensor())
train_loader = utils.data.DataLoader(dataset, shuffle=True)

# initialise the wandb logger and name your wandb project
wandb_logger = WandbLogger(project='my-awesome-project')

# add your batch size to the wandb config
wandb_logger.experiment.config["batch_size"] = batch_size

# pass wandb_logger to the Trainer
trainer = pl.Trainer(limit_train_batches=750, max_epochs=5, logger=wandb_logger)

# train the model
trainer.fit(model=autoencoder, train_dataloaders=train_loader)

# [optional] finish the wandb run, necessary in notebooks
wandb.finish()`,
    colabLink: 'https://wandb.me/lightning',
  },
  {
    iconWithName: (
      <>
        <QuickStartIcon name="keras-logo" />
        <span>Keras / </span>
        <QuickStartIcon name="tensorflow-logo" />
        <span>TensorFlow</span>
      </>
    ),
    value: FrameworkName.KERAS_TENSOR_FLOW,
    dataTest: 'quickstart-experiment-keras-tensorflow',
    pathname: FrameworkPathname.KERAS_TENSOR_FLOW,
    frameworkCategory: FrameworkCategory.MODELS,
    codeSnippet: `# This script needs these libraries to be installed:
#   tensorflow, numpy

import wandb
from wandb.keras import WandbMetricsLogger, WandbModelCheckpoint

import random
import numpy as np
import tensorflow as tf


# Start a run, tracking hyperparameters
wandb.init(
    # set the wandb project where this run will be logged
    project="my-awesome-project",

    # track hyperparameters and run metadata with wandb.config
    config={
        "layer_1": 512,
        "activation_1": "relu",
        "dropout": random.uniform(0.01, 0.80),
        "layer_2": 10,
        "activation_2": "softmax",
        "optimizer": "sgd",
        "loss": "sparse_categorical_crossentropy",
        "metric": "accuracy",
        "epoch": 8,
        "batch_size": 256
    }
)

# [optional] use wandb.config as your config
config = wandb.config

# get the data
mnist = tf.keras.datasets.mnist
(x_train, y_train), (x_test, y_test) = mnist.load_data()
x_train, x_test = x_train / 255.0, x_test / 255.0
x_train, y_train = x_train[::5], y_train[::5]
x_test, y_test = x_test[::20], y_test[::20]
labels = [str(digit) for digit in range(np.max(y_train) + 1)]

# build a model
model = tf.keras.models.Sequential([
    tf.keras.layers.Flatten(input_shape=(28, 28)),
    tf.keras.layers.Dense(config.layer_1, activation=config.activation_1),
    tf.keras.layers.Dropout(config.dropout),
    tf.keras.layers.Dense(config.layer_2, activation=config.activation_2)
    ])

# compile the model
model.compile(optimizer=config.optimizer,
              loss=config.loss,
              metrics=[config.metric]
              )

# WandbMetricsLogger will log train and validation metrics to wandb
# WandbModelCheckpoint will upload model checkpoints to wandb
history = model.fit(x=x_train, y=y_train,
                    epochs=config.epoch,
                    batch_size=config.batch_size,
                    validation_data=(x_test, y_test),
                    callbacks=[
                      WandbMetricsLogger(log_freq=5),
                      WandbModelCheckpoint("models")
                    ])

# [optional] finish the wandb run, necessary in notebooks
wandb.finish()`,
    colabLink: 'https://wandb.me/intro-keras',
  },
  {
    iconWithName: (
      <>
        <QuickStartIcon name="joyce-transformers-logo" />
        <span>Transformers</span>
      </>
    ),
    value: FrameworkName.TRANSFORMERS,
    dataTest: 'quickstart-experiment-transformers',
    pathname: FrameworkPathname.TRANSFORMERS,
    frameworkCategory: FrameworkCategory.MODELS,
    codeSnippet: `# This script needs these libraries to be installed:
#   numpy, transformers, datasets

import wandb

import os
import numpy as np
from datasets import load_dataset
from transformers import TrainingArguments, Trainer
from transformers import AutoTokenizer, AutoModelForSequenceClassification


def tokenize_function(examples):
    return tokenizer(examples["text"], padding="max_length", truncation=True)

def compute_metrics(eval_pred):
    logits, labels = eval_pred
    predictions = np.argmax(logits, axis=-1)
    return {"accuracy": np.mean(predictions == labels)}


# download prepare the data
dataset = load_dataset("yelp_review_full")
tokenizer = AutoTokenizer.from_pretrained("distilbert-base-uncased")

small_train_dataset = dataset["train"].shuffle(seed=42).select(range(1000))
small_eval_dataset = dataset["test"].shuffle(seed=42).select(range(300))

small_train_dataset = small_train_dataset.map(tokenize_function, batched=True)
small_eval_dataset = small_train_dataset.map(tokenize_function, batched=True)

# download the model
model = AutoModelForSequenceClassification.from_pretrained("distilbert-base-uncased", num_labels=5)

# set the wandb project where this run will be logged
os.environ["WANDB_PROJECT"]="my-awesome-project"

# save your trained model checkpoint to wandb
os.environ["WANDB_LOG_MODEL"]="true"

# turn off watch to log faster
os.environ["WANDB_WATCH"]="false"

# pass "wandb" to the 'report_to' parameter to turn on wandb logging
training_args = TrainingArguments(
    output_dir='models',
    report_to="wandb",
    logging_steps=5,
    per_device_train_batch_size=32,
    per_device_eval_batch_size=32,
    evaluation_strategy="steps",
    eval_steps=20,
    max_steps = 100,
    save_steps = 100
)

# define the trainer and start training
trainer = Trainer(
    model=model,
    args=training_args,
    train_dataset=small_train_dataset,
    eval_dataset=small_eval_dataset,
    compute_metrics=compute_metrics,
)
trainer.train()

# [optional] finish the wandb run, necessary in notebooks
wandb.finish()`,
    colabLink: 'https://wandb.me/hf',
  },
  {
    iconWithName: (
      <>
        <QuickStartIcon name="joyce-xgboost-logo" />
        <span>XGBoost</span>
      </>
    ),
    value: FrameworkName.XG_BOOST,
    dataTest: 'quickstart-experiment-xgboost',
    pathname: FrameworkPathname.XG_BOOST,
    frameworkCategory: FrameworkCategory.MODELS,
    codeSnippet: `# This script needs these libraries to be installed:
#   numpy, xgboost

import wandb
from wandb.xgboost import WandbCallback

import numpy as np
import xgboost as xgb


# setup parameters for xgboost
param = {
    "objective" : "multi:softmax",
    "eta" : 0.1,
    "max_depth": 6,
    "nthread" : 4,
    "num_class" : 6
}

# start a new wandb run to track this script
wandb.init(
    # set the wandb project where this run will be logged
    project="my-awesome-project",

    # track hyperparameters and run metadata
    config=param
)

# download data from wandb Artifacts and prep data
wandb.use_artifact('wandb/intro/dermatology_data:v0', type='dataset').download('.')
data = np.loadtxt(
    "./dermatology.data", delimiter=",",
    converters={33: lambda x: int(x == "?"), 34: lambda x: int(x) - 1},
)
sz = data.shape

train = data[: int(sz[0] * 0.7), :]
test = data[int(sz[0] * 0.7) :, :]

train_X = train[:, :33]
train_Y = train[:, 34]

test_X = test[:, :33]
test_Y = test[:, 34]

xg_train = xgb.DMatrix(train_X, label=train_Y)
xg_test = xgb.DMatrix(test_X, label=test_Y)
watchlist = [(xg_train, "train"), (xg_test, "test")]

# add another config to the wandb run
num_round = 5
wandb.config["num_round"] = 5
wandb.config["data_shape"] = sz

# pass WandbCallback to the booster to log its configs and metrics
bst = xgb.train(
    param, xg_train, num_round, evals=watchlist,
    callbacks=[WandbCallback()]
)

# get prediction
pred = bst.predict(xg_test)
error_rate = np.sum(pred != test_Y) / test_Y.shape[0]

# log your test metric to wandb
wandb.summary["Error Rate"] = error_rate

# [optional] finish the wandb run, necessary in notebooks
wandb.finish()
      `,
    colabLink: 'https://wandb.me/xgboost',
  },
  {
    iconWithName: (
      <>
        <QuickStartIcon name="scikit-logo" />
        <span>Sci-Kit Learn</span>
      </>
    ),
    value: FrameworkName.SCIKIT_LEARN,
    dataTest: 'quickstart-experiment-scikit',
    pathname: FrameworkPathname.SCIKIT_LEARN,
    frameworkCategory: FrameworkCategory.MODELS,
    codeSnippet: `# This script needs these libraries to be installed:
#   numpy, sklearn

import wandb
from wandb.sklearn import plot_precision_recall, plot_feature_importances
from wandb.sklearn import plot_class_proportions, plot_learning_curve, plot_roc

import numpy as np
from sklearn import datasets
from sklearn.ensemble import RandomForestClassifier
from sklearn.model_selection import train_test_split


# load and process data
wbcd = datasets.load_breast_cancer()
feature_names = wbcd.feature_names
labels = wbcd.target_names

test_size = 0.2
X_train, X_test, y_train, y_test = train_test_split(wbcd.data, wbcd.target, test_size=test_size)

# train model
model = RandomForestClassifier()
model.fit(X_train, y_train)
model_params = model.get_params()

# get predictions
y_pred = model.predict(X_test)
y_probas = model.predict_proba(X_test)
importances = model.feature_importances_
indices = np.argsort(importances)[::-1]

# start a new wandb run and add your model hyperparameters
wandb.init(project='my-awesome-project', config=model_params)

# Add additional configs to wandb
wandb.config.update({"test_size" : test_size,
                    "train_len" : len(X_train),
                    "test_len" : len(X_test)})

# log additional visualisations to wandb
plot_class_proportions(y_train, y_test, labels)
plot_learning_curve(model, X_train, y_train)
plot_roc(y_test, y_probas, labels)
plot_precision_recall(y_test, y_probas, labels)
plot_feature_importances(model)

# [optional] finish the wandb run, necessary in notebooks
wandb.finish()`,
    colabLink: 'https://wandb.me/scikit-colab',
  },
  {
    iconWithName: (
      <>
        <QuickStartIcon name="openai-logo" />
        <span>OpenAI API</span>
      </>
    ),
    value: FrameworkName.OPENAI,
    dataTest: 'quickstart-experiment-openai',
    pathname: FrameworkPathname.OPENAI,
    frameworkCategory: FrameworkCategory.LLMS,
    codeSnippet: `import datetime
from wandb.sdk.data_types.trace_tree import Trace
import openai
import wandb
wandb.init(project="custom-chained-trace-example")

# boilerplate for openai

model_name='gpt-3.5-turbo'
temperature=0.7
system_message="You are a helpful assistant that always parses the user's query and replies in 3 concise bullet points using markdown."
docs = ['This is a document about cats. Cats are furry animals that like to eat mice. Cats are also very independent animals.']

def call_gpt(model_name, temperature, system_message, query):
    messages=[
      {"role": "system", "content": system_message},
      {"role": "user", "content": query}
    ]
    response = openai.ChatCompletion.create(model=model_name,
                                        messages=messages,
                                        temperature=temperature)

    llm_end_time_ms = round(datetime.datetime.now().timestamp() * 1000)
    response_text = response["choices"][0]["message"]["content"]
    token_usage = response["usage"].to_dict()
    return llm_end_time_ms, response_text, token_usage

def chunk(docs, chunk_size=1):
    for i in range(0, len(docs), chunk_size):
        yield docs[i:i + chunk_size]

# logic to create a trace for each doc

for doc in docs:
    start_time_ms = round(datetime.datetime.now().timestamp() * 1000)
    # Create a root span to represent the entire trace
    root_span = Trace(
      name="LLMChain",
      kind="chain",
      start_time_ms=start_time_ms)

    for i, chunk in enumerate(chunk(doc, chunk_size=100)):
        doc_query = "Parse this doc: " + chunk
        start_time_ms = round(datetime.datetime.now().timestamp() * 1000)
        llm_end_time_ms, response_text, token_usage = call_gpt(model_name, temperature, system_message, doc_query)
        # Create a span to represent each LLM call
        # Add intermediate outputs to the table
        wandb.log({f'intermediate.{i}': response_text}, commit=False)

        llm_span = Trace(
            name="OpenAI",
            kind="llm",
            status_code="success",
            metadata={"temperature":temperature,
                        "token_usage": token_usage,
                        "model_name":model_name},
            start_time_ms=start_time_ms,
            end_time_ms=llm_end_time_ms,
            inputs={"system_prompt":system_message, "query": doc_query},
            outputs={"response": response_text},
            )
        root_span.add_child(llm_span)

    # update the end time of the Chain span
    root_span.add_inputs_and_outputs(
        inputs={"query": doc},
        outputs={"response": response_text})

    # update the Chain span's end time
    root_span._span.end_time_ms = llm_end_time_ms

    # add metadata to the trace table
    accuracy = 0.7
    wandb.log({"accuracy": accuracy, 'overall_f1': 0.9}, commit=False)
    # log all spans to W&B by logging the root span
    root_span.log(name="docs_trace")`,
    colabLink:
      'https://colab.research.google.com/github/wandb/examples/blob/master/colabs/prompts/WandB_Prompts_Quickstart.ipynb',
  },
  {
    iconWithName: (
      <>
        <span>🦜️🔗</span>
        <span>LangChain</span>
      </>
    ),
    value: FrameworkName.LANGCHAIN,
    dataTest: 'quickstart-experiment-langchain',
    pathname: FrameworkPathname.LANGCHAIN,
    frameworkCategory: FrameworkCategory.LLMS,
    codeSnippet: `import os
import wandb
# turn on wandb logging for langchain
os.environ["LANGCHAIN_WANDB_TRACING"] = "true"
os.environ["WANDB_PROJECT"] = "my-wandb-llm-project"

from langchain.chat_models import ChatOpenAI
from langchain.agents import load_tools, initialize_agent, AgentType

llm = ChatOpenAI(temperature=0)
tools = load_tools(["llm-math"], llm=llm)
math_agent = initialize_agent(tools,
                              llm,
                              agent=AgentType.ZERO_SHOT_REACT_DESCRIPTION)

questions = [
  "Find the square root of 5.4.",
  "What is 3 divided by 7.34 raised to the power of pi?",
  "What is the sin of 0.47 radians, divided by the cube root of 27?"
]
with wandb.init(project='langchain-tracing') as run:
    for question in questions:
        try:
            # optionally log some metadata
            run.log({'custom': 'hello'}, commit=False)
            # call your Agent as normal
            answer = math_agent.run(question)
        except Exception as e:
            # any errors will be also logged to Weights & Biases
            print(e)
            pass`,
    colabLink:
      'https://colab.research.google.com/github/wandb/examples/blob/master/colabs/prompts/WandB_Prompts_Quickstart.ipynb',
  },
  {
    iconWithName: (
      <>
        <span>🦙</span>
        <span>LlamaIndex</span>
      </>
    ),
    value: FrameworkName.LLAMAINDEX,
    dataTest: 'quickstart-experiment-llamaindex',
    pathname: FrameworkPathname.LLamaIndex,
    frameworkCategory: FrameworkCategory.LLMS,
    codeSnippet: `from llama_index import VectorStoreIndex
from llama_index import SimpleDirectoryReader
from llama_index import set_global_handler

# Set up logging
set_global_handler("wandb", run_args={"project": "llamaindex"})

## Your LlamaIndex code
docs = SimpleDirectoryReader("data").load_data()
index = VectorStoreIndex.from_documents(docs)
query_engine = index.as_query_engine()
response = query_engine.query("How do I log llama-index to wandb?")`,
    colabLink:
      'https://colab.research.google.com/github/wandb/examples/blob/master/colabs/prompts/WandB_Prompts_Quickstart.ipynb',
  },
];
